import {Formik, Form} from 'formik'
import React, {useEffect, useState} from 'react'
import {SelectInput} from '../../widgets/components/Input/SelectInput'
import TextInput from '../../widgets/components/Input/TextInput'
import {CustomButton} from '../../widgets/components/UI/CustomButton'
import {InfoCard} from '../../widgets/components/UI/InfoCard'
import {useIntl} from 'react-intl'
import {createRound, getLocation} from '../core/_requests'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '../../auth'
import {roundTypeOptions} from '../core/_constants'
import {toast} from 'react-toastify'
import {useInitializeRoundSchema} from '../../../hooks/useInitializeRoundSchema'
import {roundInitialValues} from '../../../core/_constants'
import {useCurrency} from '../../../hooks/useCurrency'
import {GTMEvent, loginSuccessMeta, loginSuccessMetaWithRound} from '../../../hooks/gtmHelpers'
import TagManager from 'react-gtm-module'
import brandingConfig from '../../../../config/brandingConfig'
import {removeNullFields} from '../../../core/_util'

export const InitializeRound = () => {
  const [ipStackData, setIpStackData] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState('USD')
  const {formatMessage} = useIntl()
  const navigate = useNavigate()
  const {
    setNewCompany,
    companyId,
    currentUser,
    selected,
    currencyBill,
    currentState,
    companyQueuePosition,
  } = useAuth()
  const {intializeRoundSchema} = useInitializeRoundSchema()
  const {currencyOptions, defaultCurrency} = useCurrency()

  useEffect(() => {
    const fetchIpStackData = async () => {
      try {
        const ipStackData = await getLocation()
        setIpStackData(ipStackData.data)
      } catch (err) {
        console.log(err)
      }
    }
    fetchIpStackData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: any) => {
    try {
      setLoading(true)
      if (!companyId) {
        setLoading(false)
        throw formatMessage({id: 'Company ID is required.'})
      }

      const {
        data: {success, errors},
      } = await createRound(
        values.roundName,
        values.roundType,
        Number(values.amountTargeted),
        Number(values.amountAchieved),
        values.currency,
        Number(companyId)
      )

      if (success) {
        setLoading(false)

        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.OnboardingRoundDetailsFilled,
            action: 'normal',
            metadata: removeNullFields({
              country_name: ipStackData?.country_name || 'NA',
              region_name: ipStackData?.region_name || 'NA',
              city_name: ipStackData?.city || 'NA',
              postcode: ipStackData?.zip || 111111,
              ...loginSuccessMetaWithRound(currentUser, {
                round_name: values.roundName,
                round_type: values.roundType,
                amount_targeted: values.amountTargeted,
                amount_achieved: values.amountAchieved,
                currency: selectedCurrency,
              }),
            }),
          },
        })

        const isEOEnabled =
          localStorage.getItem('eo') ?? process.env.REACT_APP_EXPRESS_ONBOARDING_ENABLED

        setTimeout(() => {
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.OnboardingCompletedSuccessful,
              metadata: removeNullFields({
                express_onboarding: isEOEnabled === 'true' ? true : false,
                country_name: ipStackData?.country_name || 'NA',
                region_name: ipStackData?.region_name || 'NA',
                city_name: ipStackData?.city || 'NA',
                postcode: ipStackData?.zip || 111111,
                ...loginSuccessMeta(
                  currentUser,
                  `${
                    +companyQueuePosition! > 0 ? 'Basic' : selected
                  }-${currencyBill}-${currentState}`
                ),
              }),
            },
          })
        }, 150)

        setTimeout(() => {
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.LoginSuccess,
              metadata: removeNullFields({
                country_name: ipStackData?.country_name || 'NA',
                region_name: ipStackData?.region_name || 'NA',
                city_name: ipStackData?.city || 'NA',
                postcode: ipStackData?.zip || 111111,
                ...loginSuccessMeta(currentUser),
              }),
            },
          })
        }, 300)

        setNewCompany(undefined)

        navigate('/dashboard')

        //navigate('/onboarding/team-members')
        // navigate('/dashboard')
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.OnboardingRoundDetailsFilledFailed,
            action: 'normal',
            metadata: removeNullFields({
              country_name: ipStackData?.country_name || 'NA',
              region_name: ipStackData?.region_name || 'NA',
              city_name: ipStackData?.city || 'NA',
              postcode: ipStackData?.zip || 111111,
              ...loginSuccessMetaWithRound(currentUser, {
                round_name: values.roundName,
                round_type: values.roundType,
                amount_targeted: values.amountTargeted,
                amount_achieved: values.amountAchieved,
                currency: selectedCurrency,
              }),

              error: errors.join(', '),
            }),
          },
        })

        errors.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
      console.error(error)

      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.OnboardingRoundDetailsFilledFailed,
          action: 'normal',
          metadata: removeNullFields({
            country_name: ipStackData?.country_name || 'NA',
            region_name: ipStackData?.region_name || 'NA',
            city_name: ipStackData?.city || 'NA',
            postcode: ipStackData?.zip || 111111,
            ...loginSuccessMetaWithRound(currentUser, {
              round_name: values.roundName,
              round_type: values.roundType,
              amount_targeted: values.amountTargeted,
              amount_achieved: values.amountAchieved,
              currency: selectedCurrency,
            }),
            error: error,
          }),
        },
      })
    }
  }

  return (
    <>
      <Formik
        validationSchema={intializeRoundSchema}
        initialValues={{
          ...roundInitialValues,
          currency: defaultCurrency?.toString() ?? 'USD',
        }}
        onSubmit={onSubmit}
      >
        {(formik) => {
          roundInitialValues.currency = defaultCurrency?.toString() ?? 'USD'
          formik.values.currency !== '' &&
            currencyOptions.some((currency: any) => {
              if (currency.id === parseInt(formik.values.currency)) {
                setSelectedCurrency(currency.name.substring(0, 4).trim())
                return true
              }
              return null
            })

          return (
            <Form className='company-container d-md-flex flex-md-row d-flex flex-column justify-content-md-between col-12'>
              <div className='mb-md-11 col-md-6 col-12 mt-4'>
                <h1 className='text-dark fw-bolder mb-3'>
                  {formatMessage({id: 'Initialize Round'})}
                </h1>
                <div className='text-gray-500   fs-4'>
                  {formatMessage({id: 'Add an ongoing funding details'})}
                </div>
                <div className='d-md-none d-flex w-md-50 mt-5 mt-md-0'>
                  <InfoCard
                    title={formatMessage({id: 'What is a funding round?'})}
                    desc={formatMessage({
                      id: `Funding round details in ${brandingConfig.brandName} is needed to initialize the Investor CRM & keep the targeted investors at one place.`,
                    })}
                    slug={brandingConfig.helpLinks.onBoardingInitializeRound}
                  />
                </div>
                <div className='mt-md-20 pt-md-1 mt-4'>
                  <TextInput
                    fieldType={'text'}
                    label={formatMessage({id: 'Round Name'})}
                    fieldName={'roundName'}
                    placeholder={formatMessage({
                      id: 'Enter name of the investment round',
                    })}
                    formik={formik}
                    margin={'me-md-15'}
                    toolTipText={formatMessage({id: 'GLOBAL.TOOLTIP.INITIALIZE_ROUND.ROUND_NAME'})}
                    width={11}
                    isStarRequired={true}
                  />
                  <TextInput
                    fieldType={'number'}
                    label={`${formatMessage({id: 'Amount targeted'})}(${selectedCurrency})`}
                    fieldName={'amountTargeted'}
                    placeholder={`${formatMessage({
                      id: 'Enter amount in ',
                    })}${selectedCurrency}`}
                    formik={formik}
                    margin={'me-md-15'}
                    width={11}
                    toolTipText={formatMessage({
                      id: 'GLOBAL.TOOLTIP.INITIALIZE_ROUND.AMOUNT_TARGETED',
                    })}
                    isStarRequired={true}
                  />

                  <SelectInput
                    label={formatMessage({id: 'Round Type'})}
                    fieldName={'roundType'}
                    placeholder={formatMessage({id: 'Select the round type'})}
                    formik={formik}
                    toolTipText={formatMessage({id: 'GLOBAL.TOOLTIP.INITIALIZE_ROUND.ROUND_TYPE'})}
                    margin={'me-md-14'}
                    options={roundTypeOptions}
                    isTooltipNotRequired={false}
                    isStarRequired={false}
                  />
                </div>
              </div>
              <div className='col-md-6 col-12 d-md-flex flex-md-column justify-content-between '>
                <div>
                  <div className='d-md-flex d-none '>
                    <InfoCard
                      title={formatMessage({id: 'What is a funding round?'})}
                      desc={formatMessage({
                        id: `Funding round details in ${brandingConfig.brandName} is needed to initialize the Investor CRM & keep the targeted investors at one place.`,
                      })}
                      slug={brandingConfig.helpLinks.founderMarketEditDeal}
                    />
                  </div>
                  <SelectInput
                    label={formatMessage({id: 'Select Currency'})}
                    fieldName={'currency'}
                    placeholder={formatMessage({id: 'Select the currency'})}
                    formik={formik}
                    toolTipText={formatMessage({
                      id: 'GLOBAL.TOOLTIP.INITIALIZE_ROUND.SELECT_CURRENCY',
                    })}
                    options={currencyOptions}
                  />

                  <TextInput
                    fieldType={'number'}
                    label={`${formatMessage({id: 'Amount achieved'})}(${selectedCurrency})`}
                    fieldName={'amountAchieved'}
                    placeholder={`${formatMessage({
                      id: 'Enter amount in ',
                    })}${selectedCurrency}`}
                    formik={formik}
                    margin='me-4'
                    toolTipText={formatMessage({
                      id: 'GLOBAL.TOOLTIP.INITIALIZE_ROUND.AMOUNT_ACHIEVED',
                    })}
                    width={12}
                    isStarRequired={true}
                  />
                </div>
                <div className='mt-20 d-flex justify-content-end '>
                  <CustomButton
                    isSubmitting={formik.isSubmitting}
                    isValid={formik.isValid}
                    buttonText={formatMessage({id: 'Continue'})}
                    loading={loading}
                    width={2}
                    widthLoading={4}
                    height={44}
                  />
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}
